import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, vModelText as _vModelText, withModifiers as _withModifiers, withKeys as _withKeys, createElementVNode as _createElementVNode, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, vShow as _vShow, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, renderSlot as _renderSlot, createSlots as _createSlots, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "combobox" }
const _hoisted_2 = ["id", "placeholder", "aria-invalid", "aria-describedby", "aria-activedescendant"]
const _hoisted_3 = ["id"]
const _hoisted_4 = ["aria-label"]
const _hoisted_5 = ["id", "aria-selected", "onClick", "onKeyup"]

import { computed, inject, nextTick, onMounted, ref, useSlots, watch } from 'vue';
import { useCitiesResource } from '@src/components/shared/form_fields/useCitiesResource';
import type { CityAutocompleteResource } from '@src/api/CityAutocompleteResource';
import { NullCityAutocompleteResource } from '@src/api/CityAutocompleteResource';
import { updateAutocompleteScrollPosition } from '@src/components/shared/form_fields/updateAutocompleteScrollPosition';
import { useAriaDescribedby } from '@src/components/shared/composables/useAriaDescribedby';
import { autoscrollMaxWidth, useAutocompleteScrollIntoViewOnFocus } from '@src/components/shared/form_fields/useAutocompleteScrollIntoViewOnFocus';
import FieldContainer from '@src/components/patterns/FieldContainer.vue';
import { useValueEqualsPlaceholderWarning } from '@src/components/shared/composables/useValueEqualsPlaceholderWarning';
import { useI18n } from 'vue-i18n';

enum InteractionState {
	Typing,
	Selecting,
}

interface Props {
	modelValue: string;
	inputId: string;
	scrollTargetId: string;
	label: String;
	showError: boolean;
	errorMessage: String;
	postcode: string;
	isMaxWidthField?: boolean;
	ariaDescribedby?: string | undefined;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'CityAutocompleteField',
  props: {
    modelValue: {},
    inputId: {},
    scrollTargetId: {},
    label: {},
    showError: { type: Boolean },
    errorMessage: {},
    postcode: {},
    isMaxWidthField: { type: Boolean },
    ariaDescribedby: {}
  },
  emits: [ 'field-changed', 'update:modelValue' ],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;
const { t } = useI18n();
const slots = useSlots();

const city = ref<string>( props.modelValue );
const autocompleteIsActive = ref<Boolean>( false );
const { cities, fetchCitiesForPostcode } = useCitiesResource( inject<CityAutocompleteResource>( 'cityAutocompleteResource', NullCityAutocompleteResource ) );
const activeCity = ref<string>();
const activeCityId = computed<number>( () => cities.value.indexOf( activeCity.value ) );
const interactionState = ref<InteractionState>( InteractionState.Typing );
const scrollElement = ref<HTMLElement>();

const valueEqualsPlaceholderWarning = useValueEqualsPlaceholderWarning( city, t( 'donation_form_city_placeholder' ), 'donation_form_city_placeholder_warning' );
const ariaDescribedby = useAriaDescribedby(
	props.inputId,
	computed<boolean>( () => false ),
	computed<boolean>( () => props.showError ),
	computed<boolean>( () => valueEqualsPlaceholderWarning.hasWarning.value || !!slots.message ),
	computed<string | undefined>( () => props.ariaDescribedby )
);

const scrollIntoView = useAutocompleteScrollIntoViewOnFocus( props.scrollTargetId, autoscrollMaxWidth );

const placeholder = computed( () => {
	if ( cities.value.length > 0 ) {
		return 'form_autocomplete_prompt';
	}
	return 'form_for_example';
} );

const onFocus = ( event: Event ) => {
	autocompleteIsActive.value = true;
	scrollIntoView();
	( event.target as HTMLInputElement ).select();
};

const onKeydown = ( event: KeyboardEvent ) => {
	if ( [ 'ArrowUp', 'ArrowDown', 'Tab', 'Enter' ].includes( event.key ) ) {
		return true;
	}

	interactionState.value = InteractionState.Typing;
	activeCity.value = undefined;
};

const onKeyArrows = async ( direction: 'up' | 'down' ) => {
	interactionState.value = InteractionState.Selecting;

	if ( activeCity.value === undefined ) {
		activeCity.value = cities.value[ 0 ];
		return;
	}

	let index = cities.value.findIndex( x => x === activeCity.value );

	if ( direction === 'up' && index > 0 ) {
		index--;
	}

	if ( direction === 'down' && index + 1 < cities.value.length ) {
		index++;
	}

	activeCity.value = cities.value[ index ];

	await nextTick();
	updateAutocompleteScrollPosition( scrollElement );
};

const onKeySubmit = () => {
	if ( interactionState.value === InteractionState.Typing ) {
		return;
	}

	city.value = activeCity.value;
};

let itemWasJustSelectedFromList = false;

const onInput = (): void => {
	if ( props.showError ) {
		emit( 'field-changed' );
	}
};

const onBlur = () => {
	setTimeout( () => {
		autocompleteIsActive.value = false;
		if ( !itemWasJustSelectedFromList ) {
			emit( 'field-changed' );
		}
		itemWasJustSelectedFromList = false;
	}, 200 );
};

const onSelectItem = async ( newCity: string ) => {
	itemWasJustSelectedFromList = true;
	city.value = newCity;
	await nextTick();
	emit( 'field-changed' );
};

onMounted( () => {
	fetchCitiesForPostcode( props.postcode as string );
} );

watch( (): string => props.postcode as string, ( value: string ) => {
	fetchCitiesForPostcode( value );
} );

watch( city, ( newCity: string ) => {
	emit( 'update:modelValue', newCity );
} );

watch( () => props.modelValue, ( newValue: string ) => {
	city.value = newValue;
} );


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(FieldContainer, {
    "input-id": __props.inputId,
    "show-error": __props.showError,
    "is-max-width-field": __props.isMaxWidthField
  }, _createSlots({
    label: _withCtx(() => [
      _createTextVNode(_toDisplayString(__props.label), 1)
    ]),
    field: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _withDirectives(_createElementVNode("input", {
          type: "text",
          name: "city",
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((city).value = $event)),
          id: __props.inputId,
          autocomplete: "address-level2",
          placeholder: _ctx.$t( placeholder.value, { example: _ctx.$t( 'donation_form_city_placeholder' ) } ),
          "aria-controls": "cities",
          "aria-invalid": __props.showError,
          "aria-describedby": _unref(ariaDescribedby),
          "aria-autocomplete": "list",
          "aria-activedescendant": activeCity.value ? `city-${activeCityId.value}` : null,
          onFocus: onFocus,
          onBlur: onBlur,
          onInput: onInput,
          onKeydown: [
            onKeydown,
            _cache[1] || (_cache[1] = _withKeys(_withModifiers(($event: any) => (onKeyArrows('up')), ["prevent"]), ["up"])),
            _cache[2] || (_cache[2] = _withKeys(_withModifiers(($event: any) => (onKeyArrows('down')), ["prevent"]), ["down"])),
            _withKeys(onKeySubmit, ["tab"]),
            _withKeys(onKeySubmit, ["enter"])
          ]
        }, null, 40, _hoisted_2), [
          [_vModelText, city.value]
        ]),
        _createElementVNode("span", {
          class: "is-sr-only",
          id: `${__props.inputId}-selected`,
          "aria-live": "assertive"
        }, _toDisplayString(activeCity.value), 9, _hoisted_3),
        _createVNode(_Transition, { name: "fade" }, {
          default: _withCtx(() => [
            _withDirectives(_createElementVNode("div", {
              id: "cities",
              ref_key: "scrollElement",
              ref: scrollElement,
              tabindex: "-1",
              role: "listbox",
              "aria-label": _ctx.$t( 'donation_form_city_list_label' )
            }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(cities), (city, index) => {
                return (_openBlock(), _createElementBlock("button", {
                  key: city,
                  tabindex: "-1",
                  role: "option",
                  id: `city-${index}`,
                  "aria-selected": city === activeCity.value,
                  onClick: ($event: any) => (onSelectItem( city )),
                  onKeyup: _withKeys(($event: any) => (onSelectItem( city )), ["enter","space"])
                }, [
                  _createElementVNode("strong", null, _toDisplayString(__props.postcode), 1),
                  _createTextVNode(" " + _toDisplayString(city), 1)
                ], 40, _hoisted_5))
              }), 128))
            ], 8, _hoisted_4), [
              [_vShow, autocompleteIsActive.value && _unref(cities).length > 0]
            ])
          ]),
          _: 1
        })
      ])
    ]),
    error: _withCtx(() => [
      _createTextVNode(_toDisplayString(__props.errorMessage), 1)
    ]),
    _: 2
  }, [
    (_unref(valueEqualsPlaceholderWarning).hasWarning.value)
      ? {
          name: "message",
          fn: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(valueEqualsPlaceholderWarning).warning), 1)
          ]),
          key: "0"
        }
      : (_ctx.$slots.message)
        ? {
            name: "message",
            fn: _withCtx(() => [
              _renderSlot(_ctx.$slots, "message")
            ]),
            key: "1"
          }
        : undefined
  ]), 1032, ["input-id", "show-error", "is-max-width-field"]))
}
}

})